import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Camera, MapPin, Clock, Users, ArrowRight } from 'lucide-react';
import { useFooterGlow } from '../context/FooterGlowContext';
import './Portraits.css';

const Portraits = () => {
  const { triggerGlow } = useFooterGlow();

  return (
    <div className="portraits-page">
      {/* Hero Section */}
      <section className="page-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/M7402640.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Portrait Photography
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Capturing your personality and creating timeless memories in beautiful outdoor settings
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="service-details section">
        <div className="container">
          <div className="details-grid">
            <motion.div 
              className="details-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Beautiful Outdoor Portraits</h2>
              <p>
                At Vellichor Visions, we believe the best portraits are captured in natural light 
                and beautiful outdoor settings. No studio required - we'll come to your preferred 
                location or suggest stunning spots throughout Scottsdale and Phoenix.
              </p>
              <p>
                Whether you're looking for individual portraits, family photos, engagement sessions, 
                or milestone celebrations, we'll work with you to create images that reflect your 
                personality and tell your story.
              </p>
              
              <div className="service-features">
                <div className="feature-item">
                  <Heart size={24} />
                  <div>
                    <h4>Personalized Experience</h4>
                    <p>Every session is tailored to your vision and style preferences</p>
                  </div>
                </div>
                <div className="feature-item">
                  <MapPin size={24} />
                  <div>
                    <h4>Location Flexibility</h4>
                    <p>We'll travel to your preferred location or suggest beautiful outdoor spots</p>
                  </div>
                </div>
                <div className="feature-item">
                  <Camera size={24} />
                  <div>
                    <h4>Natural Light</h4>
                    <p>Beautiful, flattering lighting that enhances your natural beauty</p>
                  </div>
                </div>
                <div className="feature-item">
                  <Clock size={24} />
                  <div>
                    <h4>Flexible Scheduling</h4>
                    <p>We work around your schedule, including golden hour sessions</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="details-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7404479.jpg`}
                alt="Beautiful outdoor portrait in desert setting"
                className="portrait-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section className="portfolio-section section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Portfolio Gallery</h2>
            <p>Explore some of our beautiful portrait sessions</p>
          </motion.div>

          <div className="portfolio-grid">
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/CP101810.jpg`}
                alt="Beach portrait during golden hour"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7404896.jpg`}
                alt="Portrait session in desert landscape"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/IMG_8692.jpg`}
                alt="Beach portrait at sunset"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7405016.jpg`}
                alt="Couple portrait in desert sunset"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7404479.jpg`}
                alt="Golden hour portrait photography"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7404853.jpg`}
                alt="Portrait photography session"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What's Included</h2>
            <p>Everything you need for a perfect portrait session</p>
          </motion.div>

          <div className="included-layout">
            <motion.div 
              className="included-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="included-items-grid">
                <motion.div 
                  className="included-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="item-icon">
                    <Users size={32} />
                  </div>
                  <h3>Pre-Session Consultation</h3>
                  <p>We'll discuss your vision, location preferences, and styling to ensure your session is perfect.</p>
                </motion.div>

                <motion.div 
                  className="included-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="item-icon">
                    <Camera size={32} />
                  </div>
                  <h3>Professional Photography</h3>
                  <p>1-2 hour session with professional equipment and expert guidance throughout.</p>
                </motion.div>

                <motion.div 
                  className="included-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="item-icon">
                    <Heart size={32} />
                  </div>
                  <h3>Careful Editing</h3>
                  <p>Professional editing and retouching to ensure your photos look their absolute best.</p>
                </motion.div>

                <motion.div 
                  className="included-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <div className="item-icon">
                    <Clock size={32} />
                  </div>
                  <h3>Quick Turnaround</h3>
                  <p>Your edited photos delivered within 5-10 days of your session.</p>
                </motion.div>
              </div>
            </motion.div>

            <motion.div 
              className="included-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/IMG_8703.jpg`}
                alt="Beautiful portrait session example"
                className="included-portrait-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section section">
        <div className="container">
          <motion.div 
            className="pricing-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Pricing</h2>
            <p>
              Each portrait session is unique, and pricing varies based on your specific needs. 
              Contact us for a personalized quote that fits your budget and vision.
            </p>
            <div className="pricing-cta">
              <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
                Get Your Quote
                <ArrowRight size={20} />
              </Link>
              <a href="mailto:vellichorvisions@gmail.com" className="btn btn-outline">
                Email for Pricing
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Create Beautiful Portraits?</h2>
            <p>Let's capture your personality and create memories that will last a lifetime.</p>
            <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
              Book Your Session
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Portraits; 